import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import API, { fileUrl } from '../utils/api';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C', V4 = '#2B0057';

const ArrowLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const EyeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const AwardIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" /><path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5" />
  </svg>
);

const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="0" y1="420" x2="210" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" /><circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const AdminDsa = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = () => API.get('/dsa/admin/all').then(r => setEnrollments(r.data)).finally(() => setLoading(false));
  useEffect(() => { fetchAll(); }, []);

  const handleVerify = async (id) => {
    await API.put(`/dsa/admin/verify-payment/${id}`);
    toast.success('Payment verified — program unlocked for this student');
    fetchAll();
  };

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* HEADER */}
      <section style={{ padding: '120px 0 60px', position: 'relative', overflow: 'hidden', background: 'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <Link to="/admin" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 24, transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = V2}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
          >
            <ArrowLeftIcon /> Admin Dashboard
          </Link>
          <div>
            <div className="section-label">ADMIN PANEL</div>
            <h1 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', fontFamily: "'Inter',sans-serif" }}>
              DSA <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Program</span>
            </h1>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 6 }}>Verify student payments to unlock the DSA Mastery Program.</p>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ paddingTop: 40 }}>
          <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)', fontSize: 15 }}>Loading…</div>
            ) : enrollments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)', fontSize: 15 }}>No enrollments yet.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
                      {['Student', 'Transaction ID', 'Screenshot', 'Status', 'Progress', 'Certificate', 'Date', 'Action'].map(h => (
                        <th key={h} style={{
                          padding: '14px 18px', textAlign: 'left',
                          color: 'var(--muted)', fontSize: 11.5, fontWeight: 800,
                          textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((e, i) => (
                      <tr key={e._id}
                        style={{ borderBottom: i < enrollments.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={ev => ev.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '15px 18px' }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{e.name}</div>
                          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>{e.email}</div>
                        </td>
                        <td style={{ padding: '15px 18px', color: 'var(--text-2)', fontSize: 13, fontFamily: 'monospace' }}>{e.transactionId}</td>
                        <td style={{ padding: '15px 18px' }}>
                          {e.paymentScreenshot && e.paymentScreenshot !== 'admin-bypass' && (
                            <a href={fileUrl(e.paymentScreenshot)} target="_blank" rel="noreferrer" style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              color: V2, fontSize: 13, fontWeight: 700, textDecoration: 'none',
                            }}>
                              <EyeIcon /> View
                            </a>
                          )}
                        </td>
                        <td style={{ padding: '15px 18px' }}>
                          <span style={{
                            display: 'inline-block', padding: '3px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 800,
                            background: e.status === 'active' ? 'rgba(46,204,113,0.12)' : 'rgba(245,166,35,0.1)',
                            color: e.status === 'active' ? '#2ECC71' : 'var(--warning)',
                            border: `1px solid ${e.status === 'active' ? 'rgba(46,204,113,0.28)' : 'rgba(245,166,35,0.28)'}`,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                          }}>
                            {e.status === 'active' ? 'Active' : 'Pending'}
                          </span>
                        </td>
                        <td style={{ padding: '15px 18px', color: 'var(--text-2)', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {e.solvedCount || 0} / {e.totalProblems || 75}
                        </td>
                        <td style={{ padding: '15px 18px' }}>
                          {e.certificateUrl ? (
                            <a href={e.certificateUrl} target="_blank" rel="noreferrer" style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              color: '#2ECC71', fontSize: 13, fontWeight: 700, textDecoration: 'none',
                            }}>
                              <AwardIcon /> {e.certificateId}
                            </a>
                          ) : (
                            <span style={{ color: 'var(--muted)', fontSize: 13 }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '15px 18px', color: 'var(--muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {new Date(e.createdAt).toLocaleDateString('en-IN')}
                        </td>
                        <td style={{ padding: '15px 18px' }}>
                          {e.status !== 'active' && (
                            <button onClick={() => handleVerify(e._id)} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '7px 13px', borderRadius: 8,
                              border: '1.5px solid rgba(46,204,113,0.35)', background: 'rgba(46,204,113,0.08)',
                              color: '#2ECC71', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
                            }}
                              onMouseEnter={ev => { ev.currentTarget.style.background = '#2ECC71'; ev.currentTarget.style.color = '#fff'; }}
                              onMouseLeave={ev => { ev.currentTarget.style.background = 'rgba(46,204,113,0.08)'; ev.currentTarget.style.color = '#2ECC71'; }}
                            >
                              <CheckIcon /> Verify Payment
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminDsa;
